import React, { useEffect, useState, useCallback } from 'react';
import { usePlaybookAnalytics } from '../hooks/usePlaybookAnalytics';
import type { AnalyticsDateRange } from '../types/playbook.types';

interface AnalyticsDashboardProps {
  playbookId: string;
  slug?: string;
}

type DatePreset = '7d' | '30d' | '90d' | 'custom';

function getDateRange(preset: DatePreset): AnalyticsDateRange | undefined {
  if (preset === 'custom') return undefined;
  const to = new Date().toISOString();
  const from = new Date();
  if (preset === '7d') from.setDate(from.getDate() - 7);
  else if (preset === '30d') from.setDate(from.getDate() - 30);
  else if (preset === '90d') from.setDate(from.getDate() - 90);
  return { from: from.toISOString(), to };
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ playbookId, slug }) => {
  const { overview, bottlenecks, guardFailures, actionErrors, versionComparison, loading, error, loadAll } = usePlaybookAnalytics(playbookId, slug);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [showVersionCompare, setShowVersionCompare] = useState(false);

  useEffect(() => {
    loadAll(getDateRange(datePreset));
  }, [loadAll, datePreset]);

  const maxDwell = Math.max(...bottlenecks.map((b) => b.avgHours), 1);

  return (
    <div className="space-y-6">
      {/* Date range filter */}
      <div className="flex items-center gap-2">
        {(['7d', '30d', '90d'] as DatePreset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setDatePreset(p)}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              datePreset === p
                ? 'bg-lex-accent text-lex-accent-contrast'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {p}
          </button>
        ))}
        {versionComparison.length > 1 && (
          <button
            type="button"
            onClick={() => setShowVersionCompare(!showVersionCompare)}
            className={`ml-auto rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              showVersionCompare
                ? 'bg-violet-600 text-white'
                : 'border border-violet-300 text-violet-600 hover:bg-violet-50 dark:border-violet-600 dark:text-violet-400'
            }`}
          >
            Version Compare
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading analytics...</span>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {overview && overview.completion && (
        <>
          {/* Header metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Total Instances" value={overview.completion.totalInstances} />
            <MetricCard
              label="Completion Rate"
              value={overview.completion.totalInstances > 0
                ? `${Math.round((overview.completion.completedCount / overview.completion.totalInstances) * 100)}%`
                : '—'}
              color={overview.completion.completedCount / Math.max(overview.completion.totalInstances, 1) >= 0.8 ? 'emerald' : 'amber'}
            />
            <MetricCard
              label="Avg Completion"
              value={overview.completion.avgCompletionHours > 0 ? `${Math.round(overview.completion.avgCompletionHours)}h` : '—'}
            />
            <MetricCard
              label="SLA Compliance"
              value={(overview.slaCompliance?.length ?? 0) > 0
                ? `${Math.round(overview.slaCompliance.reduce((s, c) => s + c.complianceRate, 0) / overview.slaCompliance.length)}%`
                : '—'}
              color={(overview.slaCompliance?.length ?? 0) > 0 &&
                overview.slaCompliance.reduce((s, c) => s + c.complianceRate, 0) / overview.slaCompliance.length >= 90
                ? 'emerald' : 'amber'}
            />
          </div>

          {/* Completion funnel */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Completion Funnel</h4>
            <div className="flex items-end gap-4 h-36">
              <FunnelBar label="Started" count={overview.completion.totalInstances} total={overview.completion.totalInstances} color="bg-lex-accent" />
              <FunnelBar label="In Progress" count={overview.completion.inProgressCount} total={overview.completion.totalInstances} color="bg-cyan-500" />
              <FunnelBar label="Completed" count={overview.completion.completedCount} total={overview.completion.totalInstances} color="bg-emerald-500" />
              <FunnelBar label="Stuck" count={overview.completion.stuckCount} total={overview.completion.totalInstances} color="bg-rose-500" />
            </div>
          </div>

          {/* Trigger distribution */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Trigger Distribution</h4>
            <div className="flex items-center gap-2">
              {Object.entries(overview.triggers.percentages || {}).map(([key, pct]) => {
                if (typeof pct !== 'number' || pct === 0) return null;
                const colors: Record<string, string> = {
                  user: 'bg-lex-accent', event: 'bg-violet-500', deadline: 'bg-amber-500', auto: 'bg-cyan-500', admin_override: 'bg-rose-500',
                };
                return (
                  <div key={key} className="flex-1" title={`${key}: ${Math.round(pct)}%`}>
                    <div className="h-6 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <div className={`h-full ${colors[key] || 'bg-gray-400'} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5 block text-center">{key}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SLA compliance */}
          {(overview.slaCompliance?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">SLA Compliance</h4>
              <div className="space-y-2">
                {overview.slaCompliance.map((sla) => (
                  <div key={sla.statusSlug} className="flex items-center gap-2">
                    <span className="text-xs text-gray-700 dark:text-gray-300 w-28 truncate">{sla.statusName}</span>
                    <span className="text-[10px] text-gray-400 w-12">{sla.slaHours}h SLA</span>
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          sla.complianceRate >= 90 ? 'bg-emerald-500' :
                          sla.complianceRate >= 70 ? 'bg-amber-500' : 'bg-rose-500'
                        }`}
                        style={{ width: `${sla.complianceRate}%` }}
                      />
                    </div>
                    <span className={`text-[10px] font-medium w-10 text-right ${
                      sla.complianceRate >= 90 ? 'text-emerald-600 dark:text-emerald-400' :
                      sla.complianceRate >= 70 ? 'text-amber-600 dark:text-amber-400' :
                      'text-rose-600 dark:text-rose-400'
                    }`}>
                      {Math.round(sla.complianceRate)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Bottleneck chart */}
      {bottlenecks.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Status Dwell Times (Bottlenecks)</h4>
          <div className="space-y-2">
            {bottlenecks.map((b) => (
              <div key={b.statusSlug} className="flex items-center gap-2">
                <span className="text-xs text-gray-700 dark:text-gray-300 w-28 truncate">{b.statusName}</span>
                <div className="flex-1 h-4 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full rounded transition-all duration-500 flex items-center pl-1 ${
                      b.avgHours / maxDwell > 0.7 ? 'bg-rose-500 text-white' :
                      b.avgHours / maxDwell > 0.4 ? 'bg-amber-500 text-white' :
                      'bg-lex-accent text-lex-accent-contrast'
                    }`}
                    style={{ width: `${Math.max((b.avgHours / maxDwell) * 100, 5)}%` }}
                  >
                    <span className="text-[9px] font-medium whitespace-nowrap">{Math.round(b.avgHours)}h avg</span>
                  </div>
                </div>
                <span className="text-[10px] text-gray-400 w-16 text-right">p95: {Math.round(b.p95Hours)}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guard failure table */}
      {guardFailures.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Guard Failure Rates</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1 text-gray-500 dark:text-gray-400 font-medium">Guard</th>
                  <th className="text-left py-1 text-gray-500 dark:text-gray-400 font-medium">Transition</th>
                  <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">Fails</th>
                  <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">Total</th>
                  <th className="text-right py-1 text-gray-500 dark:text-gray-400 font-medium">Rate</th>
                </tr>
              </thead>
              <tbody>
                {guardFailures.map((g, i) => (
                  <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${g.failRate > 0.3 ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-1 text-gray-900 dark:text-white font-medium">{g.guardType}</td>
                    <td className="py-1 text-gray-500 dark:text-gray-400">{g.transitionSlug}</td>
                    <td className="py-1 text-right text-rose-600 dark:text-rose-400">{g.failCount}</td>
                    <td className="py-1 text-right text-gray-500 dark:text-gray-400">{g.totalEvaluations}</td>
                    <td className={`py-1 text-right font-medium ${g.failRate > 0.3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {Math.round(g.failRate * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Version comparison */}
      {showVersionCompare && versionComparison.length > 1 && (
        <div className="rounded-lg border border-violet-200 dark:border-violet-700 p-4">
          <h4 className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase mb-3">Version Comparison</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-violet-200 dark:border-violet-700">
                  <th className="text-left py-1 text-gray-500 font-medium">Version</th>
                  <th className="text-right py-1 text-gray-500 font-medium">Instances</th>
                  <th className="text-right py-1 text-gray-500 font-medium">Completion</th>
                  <th className="text-right py-1 text-gray-500 font-medium">Avg Hours</th>
                </tr>
              </thead>
              <tbody>
                {versionComparison.map((v) => (
                  <tr key={v.version} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-1 text-gray-900 dark:text-white font-medium">v{v.version}</td>
                    <td className="py-1 text-right text-gray-500 dark:text-gray-400">{v.instanceCount}</td>
                    <td className="py-1 text-right text-gray-700 dark:text-gray-300">{Math.round(v.completionRate * 100)}%</td>
                    <td className="py-1 text-right text-gray-700 dark:text-gray-300">{Math.round(v.avgCompletionHours)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function MetricCard({ label, value, color }: { label: string; value: string | number; color?: 'emerald' | 'amber' | 'rose' }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
      <div className={`text-xl font-bold ${
        color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
        color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
        color === 'rose' ? 'text-rose-600 dark:text-rose-400' :
        'text-gray-900 dark:text-white'
      }`}>
        {value}
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{label}</div>
    </div>
  );
}

function FunnelBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex-1 flex flex-col items-center">
      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded overflow-hidden relative" style={{ height: '80px' }}>
        <div
          className={`absolute bottom-0 w-full ${color} rounded transition-all duration-500`}
          style={{ height: `${Math.max(pct, 3)}%` }}
        />
      </div>
      <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-1">{label}</span>
      <span className="text-xs font-medium text-gray-900 dark:text-white">{count}</span>
    </div>
  );
}

export default AnalyticsDashboard;
